import type { ReactElement, ReactNode } from 'react'
import { Box } from '@mui/material'

export type CountPillTone = 'unread' | 'neutral'
export type CountPillSize = 'tab' | 'row'

interface CountPillProps {
  count: number
  tone: CountPillTone
  size?: CountPillSize
  /** Rendered before the number. */
  icon?: ReactNode
  /** Hidden by default: the parent carries the accessible text. */
  ariaHidden?: boolean
}

const TONES: Record<CountPillTone, { bgcolor: string; color: string }> = {
  unread: { bgcolor: '#fde8e8', color: '#b42318' },
  neutral: { bgcolor: '#eef1f6', color: '#5b6572' }
}

const SIZES = {
  tab: { height: 20, minWidth: 20, padding: '0 6px', fontSize: 12 },
  row: { height: 22, padding: '0 8px 0 6px', fontSize: 11.5, gap: '4px' }
} as const satisfies Record<CountPillSize, object>

export default function CountPill({ count, tone, size = 'tab', icon, ariaHidden = true }: CountPillProps): ReactElement {
  return (
    <Box
      component="span"
      aria-hidden={ariaHidden || undefined}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        borderRadius: '999px',
        fontWeight: 600,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        ...TONES[tone],
        ...SIZES[size]
      }}
    >
      {icon}
      {count}
    </Box>
  )
}
